import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { FieldConfiguration } from '@adornis/field-management/field-configuration.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html, nothing } from 'lit';
import { PersonImportAPI, PersonImportStrategyCounts } from '../api/PersonImportAPI.js';
import {
  IMPORT_FINDING_CATEGORIES,
  ImportFindingSeverity,
  type ImportFindingCode,
} from '../entities/PersonImportStrategy.js';
import { PersonImportStrategyValidation } from '../entities/PersonImportStrategyValidation.js';
import { PersonImportStrategyDataTable, type ImportReviewFilter } from './PersonImportStrategyDataTable.js';

// module-level so the reference is stable across the dialog's re-renders - the table memoizes its options on it
const STRATEGY_TABLE_FIELDS = ['approved', 'person', 'changes', 'findings', 'certainty', 'actions'].map(
  customID => new FieldConfiguration({ customID }),
);

const PersonImportStrategyOverviewDialog = stateful(
  ({ resolve, reject, processId }: { resolve: () => void; reject: () => void; processId: string }) => {
    const { value: validation, loading: validationLoading } = useQuery(
      () => PersonImportAPI.subscribeValidation(processId)(selectionSet(() => PersonImportStrategyValidation, 1)),
      [processId],
    );

    const { value: counts } = useQuery(
      () => PersonImportAPI.subscribeCounts(processId)(selectionSet(() => PersonImportStrategyCounts, 2)),
      [processId],
    );

    /**
     * Which rows the table shows. Errors first - they are the rows the import will not run until somebody
     * looked at them. The table's query is not live, so a row fixed here stays where it is.
     */
    const [review, setReview] = useState<ImportReviewFilter>({ severity: ImportFindingSeverity.FEHLER });

    const unusedFields = validation?.unusedFields ?? [];
    const errors = counts?.bySeverity?.find(count => count.severity === ImportFindingSeverity.FEHLER);
    const warnings = counts?.bySeverity?.find(count => count.severity === ImportFindingSeverity.WARNUNG);
    /**
     * Searching looks through all rows, not only the filtered ones - a person must not stay hidden because
     * "Fehler" happens to be selected. Picking a filter ends the search.
     */
    const [search, setSearch] = useState('');
    const pick = (filter: ImportReviewFilter) => {
      setSearch('');
      setReview(filter);
    };
    const isActive = (filter: ImportReviewFilter) =>
      !search && filter.severity === review.severity && filter.code === review.code;

    return html`
      <div class="flex flex-col gap-2 border-b border-base-300 px-4 pt-3 pb-2">
        ${DialogHeader({
          title: 'Import Strategie überprüfen',
          // the counts as the subtitle and the search beside the title: one header row instead of three
          subtitle: html`
            <span class="flex flex-wrap items-center gap-x-4 gap-y-1">
              <span class="flex items-center gap-1" title="Personen, die es noch nicht gibt">
                <span class="icon-[material-symbols--person-add] text-base text-success"></span>
                <span class="font-semibold tabular-nums text-success">${counts?.inserted ?? 0}</span>
                neu angelegt
              </span>
              <span class="flex items-center gap-1" title="Bestehende Personen mit Änderungen">
                <span class="icon-[material-symbols--edit] text-base text-primary"></span>
                <span class="font-semibold tabular-nums text-primary">${counts?.updated ?? 0}</span>
                aktualisiert
              </span>
              <span class="flex items-center gap-1" title="Bereits aktuell, nicht in der Liste">
                <span class="icon-[material-symbols--check] text-base"></span>
                <span class="font-semibold tabular-nums">${counts?.unchanged ?? 0}</span>
                unverändert
              </span>
              <span>Ein Klick auf die Person ordnet die Zeile einer anderen zu.</span>
            </span>
          `,
          actions: html`
            <div class="w-72 lg:w-96">
              ${TextInput({
                value: search,
                placeholder: 'In allen Spalten der Datei suchen …',
                placeholderMode: 'hide',
                ariaLabel: 'Zeilen durchsuchen',
                clearable: true,
                debounceMs: 300,
                prefix: html`<span class="icon-[material-symbols--search] opacity-60"></span>`,
                onChanged: value => setSearch(value?.trim() ?? ''),
              })}
            </div>
          `,
          close: reject,
          closeLabel: 'Dialog schließen',
        })}
        <div class="flex flex-wrap items-center gap-2" role="toolbar" aria-label="Zeilen filtern">
          <button
            class="btn btn-sm ${isActive({ severity: ImportFindingSeverity.FEHLER }) ? 'btn-error' : 'btn-ghost text-error'}"
            title="Zeilen mit Fehler werden nicht importiert, solange sie nicht geprüft wurden."
            @click=${() => pick({ severity: ImportFindingSeverity.FEHLER })}
          >
            <span class="icon-[material-symbols--flag-rounded] text-lg"></span>
            Fehler
            <span class="font-semibold tabular-nums">${errors?.open ?? 0}</span>
            ${errors?.resolved ? html`<span class="opacity-70">(${errors.resolved} behoben)</span>` : nothing}
          </button>
          <button
            class="btn btn-sm ${isActive({ severity: ImportFindingSeverity.WARNUNG }) ? 'btn-warning' : 'btn-ghost text-warning'}"
            title="Zeilen mit Warnung sollten geprüft werden."
            @click=${() => pick({ severity: ImportFindingSeverity.WARNUNG })}
          >
            <span class="icon-[material-symbols--warning-rounded] text-lg"></span>
            Warnungen
            <span class="font-semibold tabular-nums">${warnings?.open ?? 0}</span>
            ${warnings?.resolved ? html`<span class="opacity-70">(${warnings.resolved} behoben)</span>` : nothing}
          </button>
          <button
            class="btn btn-sm ${isActive({}) ? 'btn-neutral' : 'btn-ghost'}"
            title="Alle Zeilen mit Änderungen"
            @click=${() => pick({})}
          >
            Alle
            <span class="font-semibold tabular-nums">${counts?.listed ?? 0}</span>
          </button>
          <span class="mx-1 h-6 border-l border-base-300"></span>
          ${(counts?.byCode ?? []).map(count => {
            const code = count.code as ImportFindingCode;
            const active = isActive({ code });
            const tone = !count.open
              ? active
                ? 'btn-success'
                : 'btn-ghost text-success'
              : count.severity === ImportFindingSeverity.FEHLER
                ? active
                  ? 'btn-error'
                  : 'btn-soft btn-error'
                : count.severity === ImportFindingSeverity.WARNUNG
                  ? active
                    ? 'btn-warning'
                    : 'btn-soft btn-warning'
                  : active
                    ? 'btn-neutral'
                    : 'btn-soft';

            return html`
              <button class="btn btn-sm ${tone}" @click=${() => pick({ code })}>
                ${IMPORT_FINDING_CATEGORIES[code]?.label ?? code}
                <span class="font-semibold tabular-nums">${count.open ?? 0}</span>
                ${count.resolved ? html`<span class="opacity-70">(${count.resolved} behoben)</span>` : nothing}
              </button>
            `;
          })}
          ${
            !validationLoading && unusedFields.length > 0
              ? html`
                  <!-- at the end of the filters rather than an alert row of its own; the full list is in the title -->
                  <span
                    role="alert"
                    class="ml-auto flex min-w-0 items-center gap-1 text-sm text-base-content/70"
                    title=${unusedFields.join(', ')}
                  >
                    <span class="icon-[material-symbols--warning-rounded] shrink-0 text-lg text-warning"></span>
                    <span class="truncate">
                      <span class="font-semibold">${unusedFields.length} Spalte(n) ignoriert:</span>
                      ${unusedFields.join(', ')}
                    </span>
                  </span>
                `
              : nothing
          }
        </div>
      </div>
      <!-- the one scroll container, in both directions: header and footer stay in place, the scrollbars at its edges -->
      <div class="min-h-0 flex-1 overflow-auto">
        ${PersonImportStrategyDataTable({ processId, fields: STRATEGY_TABLE_FIELDS, review: search ? { search } : review })}
      </div>
      <div class="flex items-center justify-end gap-4 border-t border-base-300 px-4 py-2">
        ${
          errors?.open
            ? html`<span class="text-sm text-base-content/70">
                ${errors.open} Zeile(n) mit Fehler werden übersprungen, solange sie nicht genehmigt sind.
              </span>`
            : nothing
        }
        <button class="btn btn-success" @click=${() => resolve()}>Import ausführen</button>
      </div>
    `;
  },
);

export async function openPersonImportStrategyOverviewDialog(processId: string): Promise<void> {
  return showDialog({
    content: (resolve, reject) => PersonImportStrategyOverviewDialog({ resolve, reject, processId }),
    // fullscreen: no gutter around the box. The box itself does not scroll - the table body does - so no
    // gutter is reserved for a scrollbar it never shows (the engine panel sets `scrollbar-gutter: stable`).
    // gap-0: the design's modal-box spaces its children 1.5rem apart, here header, table and footer touch
    modalClasses: 'p-0',
    modalBoxClasses:
      'flex flex-col gap-0 h-full w-full max-h-none max-w-none overflow-hidden rounded-none border-0 p-0 [scrollbar-gutter:auto]',
  });
}
